import React from "react";

import { IOrderChangeTarget, ISchoolManager } from "../../../state";
import { ModalContext } from "../../../libs/ModalProvider";
import { MAX_CHANGE_NUM } from "../../../constants/schoolOrderChangeRequest";
import ToolTip from "../../../atoms/ToolTip";

import GradeEditDialog from "./GradeEditDialog";
import styles from "./RegistrationNumChangeRequestForm.module.scss";

export interface RegistrationNumChangeRequestFormProps {
  school?: ISchoolManager;
  calling: boolean;
  error: string;
  isDone: boolean;
  submitRequest: (
    changes: {
      entrance_year: number;
      dlp_registrable_num: number;
      g4s_registrable_num: number;
      teaching_material_names: string[];
    }[],
    reason: string,
  ) => void;
  isMasqueradeByAdmin: boolean;
}

const formatDiff = (diff: number) => (diff > 0 ? `+${diff}` : `${diff}`);

const ZERO_NUM_REASON =
  "利用可能人数が0人のため変更できません。\n新規のご契約は営業担当にご連絡ください。";
const NO_VOL_REASON =
  "購入済みの教材が登録されていないため変更できません。\n営業担当にご連絡ください。";

const REASON_PLACEHOLDER =
  "例：\n・転入により2学年の利用者が増えるため\n・転出により1学年の利用者が減るため\n・長期欠席により教材を利用しない生徒がいるため";

// grade 0 は年度更新前の翌年度入学者（新1学年）
const targetLabel = (target: IOrderChangeTarget) =>
  target.grade === 0
    ? `新1学年（${target.entrance_year}年度入学）`
    : `${target.grade}学年`;

export const RegistrationNumChangeRequestForm = (
  props: RegistrationNumChangeRequestFormProps,
) => {
  const ctx = React.useContext(ModalContext);
  const [edits, setEdits] = React.useState<{
    [entranceYear: number]: {
      dlp_registrable_num: number;
      g4s_registrable_num: number;
      teaching_material_names: string[];
    };
  }>({});
  const [isConfirm, setIsConfirm] = React.useState(false);
  const [reason, setReason] = React.useState("");

  // school未取得のままフォールバック値で描画すると誤操作につながるため何も出さない
  if (!props.school) {
    return null;
  }

  // APIが翌年度入学（grade 0）→1学年→…→max_grade の順で返す
  const targets = props.school.order_change_targets ?? [];

  const currentOf = (target: IOrderChangeTarget) => ({
    dlp: target.dlp_registrable_num,
    g4s: target.g4s_registrable_num,
  });

  const usedOf = (target: IOrderChangeTarget) => ({
    dlp: target.dlp_used_num,
    g4s: target.g4s_used_num,
  });

  const additionalVolNames = (
    target: IOrderChangeTarget,
    edit: { dlp_registrable_num: number; teaching_material_names: string[] },
  ) =>
    edit.dlp_registrable_num > target.dlp_registrable_num
      ? target.teaching_material_names
          .filter(name => edit.teaching_material_names.includes(name))
          .join("、")
      : "";

  const changedTargets = targets.filter(target => {
    const edit = edits[target.entrance_year];
    if (!edit) {
      return false;
    }
    const current = currentOf(target);
    return (
      edit.dlp_registrable_num !== current.dlp ||
      edit.g4s_registrable_num !== current.g4s
    );
  });

  const changes = changedTargets.map(target => ({
    entrance_year: target.entrance_year,
    dlp_registrable_num: edits[target.entrance_year].dlp_registrable_num,
    g4s_registrable_num: edits[target.entrance_year].g4s_registrable_num,
    teaching_material_names:
      edits[target.entrance_year].teaching_material_names,
  }));

  const canEditDlp = !!props.school?.has_dlp_order;
  const canEditG4s = !!(
    props.school?.has_g4s_order || props.school?.has_g4s_basic_order
  );

  const dlpUnavailableReasonOf = (target: IOrderChangeTarget) => {
    if (!canEditDlp) {
      return undefined;
    }
    if (target.dlp_registrable_num === 0) {
      return ZERO_NUM_REASON;
    }
    if (target.teaching_material_names.length === 0) {
      return NO_VOL_REASON;
    }
    return undefined;
  };

  const g4sUnavailableReasonOf = (target: IOrderChangeTarget) =>
    canEditG4s && target.g4s_registrable_num === 0
      ? ZERO_NUM_REASON
      : undefined;

  const editableOf = (target: IOrderChangeTarget) => ({
    dlp: canEditDlp && !dlpUnavailableReasonOf(target),
    g4s: canEditG4s && !g4sUnavailableReasonOf(target),
  });

  const hasOverLimitChange = changedTargets.some(target => {
    const current = currentOf(target);
    const edit = edits[target.entrance_year];
    return (
      Math.abs(edit.dlp_registrable_num - current.dlp) > MAX_CHANGE_NUM ||
      Math.abs(edit.g4s_registrable_num - current.g4s) > MAX_CHANGE_NUM
    );
  });

  const hasBelowUsedChange = changedTargets.some(target => {
    const used = usedOf(target);
    const edit = edits[target.entrance_year];
    return (
      edit.dlp_registrable_num < used.dlp || edit.g4s_registrable_num < used.g4s
    );
  });

  const hasPendingChange = changedTargets.some(target => !!target.pending_item);

  const openEditDialog = (target: IOrderChangeTarget) => {
    const current = currentOf(target);
    const used = usedOf(target);
    const edit = edits[target.entrance_year];
    const editable = editableOf(target);
    ctx.actions.showModal(
      <GradeEditDialog
        label={targetLabel(target)}
        canEditDlp={editable.dlp}
        canEditG4s={editable.g4s}
        dlpUnavailableReason={dlpUnavailableReasonOf(target)}
        g4sUnavailableReason={g4sUnavailableReasonOf(target)}
        currentDlp={current.dlp}
        currentG4s={current.g4s}
        usedDlp={used.dlp}
        usedG4s={used.g4s}
        initialDlp={edit ? edit.dlp_registrable_num : current.dlp}
        initialG4s={edit ? edit.g4s_registrable_num : current.g4s}
        teachingMaterialNames={target.teaching_material_names}
        initialTeachingMaterialNames={
          edit ? edit.teaching_material_names : target.teaching_material_names
        }
        onSave={(dlp, g4s, teachingMaterialNames) =>
          setEdits(prev => ({
            ...prev,
            [target.entrance_year]: {
              dlp_registrable_num: dlp,
              g4s_registrable_num: g4s,
              teaching_material_names: teachingMaterialNames,
            },
          }))
        }
        closeModal={() => ctx.actions.closeModal()}
      />,
    );
  };

  const changeList = (
    <div className={styles.changeList}>
      {changedTargets.map(target => {
        const current = currentOf(target);
        const edit = edits[target.entrance_year];
        const dlpDiff = edit.dlp_registrable_num - current.dlp;
        const g4sDiff = edit.g4s_registrable_num - current.g4s;
        return (
          <div key={target.entrance_year} className={styles.changeRow}>
            <span className={styles.gradeLabel}>{targetLabel(target)}</span>
            {canEditDlp && (
              <span>
                教材利用可能人数：
                {dlpDiff === 0
                  ? `${current.dlp}人（変更なし）`
                  : `${current.dlp}人 → ${
                      edit.dlp_registrable_num
                    }人（${formatDiff(dlpDiff)}人）`}
              </span>
            )}
            {canEditDlp && additionalVolNames(target, edit) && (
              <span>使用する教材：{additionalVolNames(target, edit)}</span>
            )}
            {canEditG4s && (
              <span>
                GEAR利用可能人数：
                {g4sDiff === 0
                  ? `${current.g4s}人（変更なし）`
                  : `${current.g4s}人 → ${
                      edit.g4s_registrable_num
                    }人（${formatDiff(g4sDiff)}人）`}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );

  if (props.isDone) {
    return (
      <div className={styles.container}>
        <div className={styles.title}>
          <p>アカウント数の変更</p>
        </div>
        <div className={styles.box}>
          <p className={styles.doneMessage}>申請を受け付けました。</p>
          <p>
            担当者が内容を確認のうえ対応いたします。
            <br />
            反映まで1~3営業日お時間をいただきます。しばらくお待ちください。
          </p>
          {changeList}
          <div className={styles.reasonConfirm}>
            申請理由：<span className={styles.reasonText}>{reason}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.title}>
        <p>アカウント数の変更</p>
        <span>学年ごとのアカウント数の変更を申請します。</span>
      </div>
      {isConfirm ? (
        <>
          <div className={styles.subTitle}>
            以下の内容で申請します。よろしいですか？
            <br />
            ※この操作は取り消しできません。
          </div>
          <div className={styles.box}>
            {changeList}
            <div className={styles.reasonConfirm}>
              申請理由：<span className={styles.reasonText}>{reason}</span>
            </div>
          </div>
          {hasOverLimitChange && (
            <div className={styles.caution}>
              現在の人数が更新されたため、申請内容を修正してください
            </div>
          )}
          {hasBelowUsedChange && (
            <div className={styles.caution}>
              利用中の生徒数を下回る変更が含まれています。対象生徒の教材利用またはGEAR利用をOFFにしてから申請してください
            </div>
          )}
          {hasPendingChange && (
            <div className={styles.caution}>
              申請中の学年が含まれています。反映が完了するまでお待ちください
            </div>
          )}
          {props.error && <div className={styles.caution}>{props.error}</div>}
          <div className={styles.btnArea}>
            <button
              className={"btn btn-secondary"}
              disabled={props.calling}
              onClick={() => setIsConfirm(false)}
            >
              修正する
            </button>
            <button
              className={"btn " + styles.okBtn}
              disabled={
                changes.length === 0 ||
                hasOverLimitChange ||
                hasBelowUsedChange ||
                hasPendingChange ||
                props.calling
              }
              onClick={() => props.submitRequest(changes, reason)}
            >
              申請する
            </button>
          </div>
        </>
      ) : (
        <>
          <div className={styles.notes}>
            <p>
              ※人数の増減は1学年・各項目（教材/GEAR）につき{MAX_CHANGE_NUM}
              人までです。それ以上の申請を行う場合は営業担当にご連絡ください。
            </p>
            <p>
              ※人数変更時は、編集画面から対象生徒の「教材対象」「GEAR拡張」を有効から無効に変更してください。
            </p>
            <p>
              ※4月末日以降のアカウント数減少の申請に対しては返金をお受けすることができません。
            </p>
          </div>
          <div className={styles.gradeTable}>
            {targets.map(target => {
              const current = currentOf(target);
              const pendingItem = target.pending_item;
              const editable = editableOf(target);
              const isEditable = editable.dlp || editable.g4s;
              const edit = edits[target.entrance_year];
              const nextDlp = pendingItem
                ? pendingItem.requested_dlp_registrable_num
                : edit
                ? edit.dlp_registrable_num
                : undefined;
              const nextG4s = pendingItem
                ? pendingItem.requested_g4s_registrable_num
                : edit
                ? edit.g4s_registrable_num
                : undefined;
              return (
                <div key={target.entrance_year} className={styles.gradeRow}>
                  <span className={styles.gradeLabel}>
                    {targetLabel(target)}
                  </span>
                  {canEditDlp && (
                    <span className={styles.gradeValue}>
                      教材利用可能人数：{current.dlp}人
                      {nextDlp !== undefined && nextDlp !== current.dlp && (
                        <span className={styles.changedValue}>
                          {` → ${nextDlp}人（${formatDiff(
                            nextDlp - current.dlp,
                          )}人）`}
                        </span>
                      )}
                    </span>
                  )}
                  {canEditDlp &&
                    !pendingItem &&
                    edit &&
                    additionalVolNames(target, edit) && (
                      <span className={styles.gradeValue}>
                        使用する教材：{additionalVolNames(target, edit)}
                      </span>
                    )}
                  {canEditG4s && (
                    <span className={styles.gradeValue}>
                      GEAR利用可能人数：{current.g4s}人
                      {nextG4s !== undefined && nextG4s !== current.g4s && (
                        <span className={styles.changedValue}>
                          {` → ${nextG4s}人（${formatDiff(
                            nextG4s - current.g4s,
                          )}人）`}
                        </span>
                      )}
                    </span>
                  )}
                  {(canEditDlp || canEditG4s) &&
                    (pendingItem ? (
                      <span className={styles.pendingLabel}>申請中</span>
                    ) : (
                      <ToolTip
                        content={
                          dlpUnavailableReasonOf(target) ??
                          g4sUnavailableReasonOf(target) ??
                          ""
                        }
                        isEnabled={!isEditable}
                        className={styles.editBtnToolTip}
                      >
                        <button
                          className={"btn btn-secondary " + styles.editBtn}
                          disabled={!isEditable}
                          onClick={() => openEditDialog(target)}
                        >
                          変更
                        </button>
                      </ToolTip>
                    ))}
                </div>
              );
            })}
          </div>
          <div className={styles.subTitle}>申請理由（必須）</div>
          <div className={styles.reasonBox}>
            <textarea
              value={reason}
              onChange={e => setReason(e.target.value)}
              className={styles.reasonTextArea}
              placeholder={REASON_PLACEHOLDER}
            />
          </div>
          {hasOverLimitChange && (
            <div className={styles.caution}>
              現在の人数が更新されたため、申請内容を修正してください
            </div>
          )}
          {hasBelowUsedChange && (
            <div className={styles.caution}>
              利用中の生徒数を下回る変更が含まれています。対象生徒の教材利用またはGEAR利用をOFFにしてから申請してください
            </div>
          )}
          {hasPendingChange && (
            <div className={styles.caution}>
              申請中の学年が含まれています。反映が完了するまでお待ちください
            </div>
          )}
          {props.isMasqueradeByAdmin && (
            <div className={styles.caution}>
              証跡管理のため、代理ログインでは申請できません。学校の管理者に申請を依頼してください
            </div>
          )}
          {props.error && <div className={styles.caution}>{props.error}</div>}
          <div className={styles.btnArea}>
            <span
              className={
                props.isMasqueradeByAdmin ? styles.notAllowedCursor : undefined
              }
            >
              <button
                className={"btn " + styles.okBtn}
                disabled={
                  props.isMasqueradeByAdmin ||
                  changes.length === 0 ||
                  reason.trim() === "" ||
                  hasOverLimitChange ||
                  hasBelowUsedChange ||
                  hasPendingChange ||
                  props.calling
                }
                onClick={() => setIsConfirm(true)}
              >
                申請内容を確認する
              </button>
            </span>
          </div>
        </>
      )}
    </div>
  );
};

export default RegistrationNumChangeRequestForm;
